import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import moment from 'moment';
import { AngularContext } from 'react-app';
import { useSelector } from 'react-redux';
import { Overlay } from 'react-bootstrap';
import t from 'react-translate';
import { useMediaQuery } from 'react-responsive';

import { RootState } from 'redux/schemas';
import { getCurrentUser, getUserTimezone } from 'redux/selectors/users';

import NvIcon from 'shared/components/nv-icon';
import useWindowResize from 'shared/hooks/use-window-resize';
import { halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import { isRtl, screenSmMax, screenXsMax } from 'styles/global_defaults/media-queries';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import TimezoneSelector from './timezone-selector';
import { config } from '../../../../config/pendo.config.json';

const POPOVER_ZINDEX = 5095; // More than left nav z-index

const styles = css`
  gap: ${quarterSpacing}px;
  cursor: pointer;

  .icon-dueday {
    font-size: ${halfSpacing}px;
  }
`;

const Clock = ({
  targetRef,
  showTimezoneSelection,
  setShowTimezoneSelection,
}: {
  targetRef: React.RefObject<HTMLDivElement>;
  showTimezoneSelection: boolean;
  setShowTimezoneSelection: (show: boolean) => void;
}) => {
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);

  const currentUserTimezone = useSelector(getUserTimezone);
  const lhsExpanded = useSelector((state: RootState) => state.app.lhsExpanded);
  const timeZoneObj = TimezonesService.timezones.find((tz) => tz.longName === currentUserTimezone);

  // To display the timestring accordingly
  const isNotDesktop = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  // To position the tooltip
  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const [time, setTime] = useState(null);

  const timeoutRef = useRef(null);

  const updateClock = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // We are not updating the clock every second, but only when the minute
    // changes or when timezone changes to avoid unnecessary re-renders
    const currentTime = moment.tz(currentUserTimezone);
    if (currentTime.minutes() !== time?.minutes()
      || currentTime.tz() !== time?.tz()) {
      setTime(currentTime);
    }

    timeoutRef.current = setTimeout(() => {
      updateClock();
    }, 1000);
  }, [currentUserTimezone, time?.tz(), time?.minutes()]);

  useEffect(() => {
    updateClock();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = null;
    };
  }, [updateClock]);

  let timeString = time?.format('HH:mm');
  if ((lhsExpanded.isLhsExpandedDesktop && !isNotDesktop)
    || (lhsExpanded.isLhsExpandedMobile && isNotDesktop)) {
    timeString = `${time?.format('L HH:mm')} ${timeZoneObj ? t.TIMEZONE[timeZoneObj.translationKey]() : ''}`;
  }

  const TooltipText = () => (
    <div className='label'>
      {time?.format('ddd L HH:mm')}<br />
      {timeZoneObj ? t.TIMEZONE[timeZoneObj.translationKey]() : ''} - {time?.format('UTCZ')}
    </div>
  );

  return (
    <div className='position-relative' ref={targetRef}>
      <NvTooltip
        // any type is required, under the hood Overlay will allow ReactNode,
        // but the text prop is defined as a string in NvTooltip
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        text={(<TooltipText />) as any}
        placement={isHandheld ? 'bottom' : 'right'}
        preventOverflow={false}
        enabled={!showTimezoneSelection}
        zIndex={POPOVER_ZINDEX}
      >
        <ClickableContainer
          css={styles}
          className='d-flex align-items-center justify-content-center py-1 mt-3 bg-yellow'
          onClick={() => setShowTimezoneSelection(!showTimezoneSelection)}
          data-qa={config.pendo.lhs.timezoneClock.openTimezoneSelector}
          aria-haspopup='true'
          aria-expanded={showTimezoneSelection}
        >
          <NvIcon icon='dueday' size='xss-smallest' />
          <div className='course-title-xxs'>{timeString}</div>
        </ClickableContainer>
      </NvTooltip>
    </div>
  );
};

const TimezoneClock = () => {
  const { displayTimeZoneClock } = useSelector(getCurrentUser);
  const targetRef = useRef(null);
  const [showTimezoneSelection, setShowTimezoneSelection] = useState(false);

  // To position the popover
  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  useWindowResize(() => {
    setShowTimezoneSelection(false);
  }, undefined, false, []);

  useEffect(() => {
    // Focus the first element in the popover when it is opened
    const inputEl = document.getElementById('timezone-selector-wrapper')?.querySelector?.('input');

    if (showTimezoneSelection && inputEl) {
      inputEl.focus();
      // Set inert attribute to the main panel to prevent focus on it
      // when the popover is opened
      document.getElementById('main-panel')?.setAttribute('inert', 'true');
    }
  }, [showTimezoneSelection]);

  const getOverlayPlacement = () => {
    if (isHandheld) {
      return isRtl() ? 'bottom-end' : 'bottom-start';
    }
    return isRtl() ? 'left-start' : 'right-start';
  };

  return (
    <React.Fragment>
      {displayTimeZoneClock && (
        <React.Fragment>
          <Clock
            targetRef={targetRef}
            showTimezoneSelection={showTimezoneSelection}
            setShowTimezoneSelection={setShowTimezoneSelection}
          />
          <Overlay
            target={targetRef.current}
            show={showTimezoneSelection}
            placement={getOverlayPlacement()}
            popperConfig={{
              modifiers: [
                {
                  name: 'hide',
                  enabled: false,
                },
                {
                  name: 'preventOverflow',
                  enabled: false,
                },
              ],
            }}
            transition={false}
            rootClose
            onHide={() => {
              setShowTimezoneSelection(false);
              // Focus the clock icon when the popover is closed
              targetRef.current?.querySelector('[data-qa="OPEN_TIMEZONE_SELECTOR"]')?.focus();
              document.getElementById('main-panel')?.removeAttribute('inert');
            }}
          >
            <div
              id='timezone-selector-wrapper'
              style={{ zIndex: POPOVER_ZINDEX }}
            >
              <TimezoneSelector />
            </div>
          </Overlay>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default TimezoneClock;


